import React from 'react';
import { ChevronUp, ChevronDown, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const DepositReportTable = ({
  records,
  sortBy,
  sortOrder,
  onSort
}) => {
  const { t } = useTranslation();


  const getPaymentMethodBadge = (method) => {
    const methodConfig = {
      'cash': 'bg-green-100 text-green-800 border-green-200',
      'bankTransfer': 'bg-blue-100 text-blue-800 border-blue-200',
      'wallet': 'bg-purple-100 text-purple-800 border-purple-200',
      'egyptianPost': 'bg-orange-100 text-orange-800 border-orange-200'
    };

    const config = methodConfig[method] || 'bg-slate-100 text-slate-800 border-slate-200';

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config}`}>
        {t(method) || method}
      </span>
    );
  };

  const SortableHeader = ({ field, children }) => (
    <th
      className={`px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors duration-200 text-start`}
      onClick={() => onSort(field)}
    >
      <div className={`flex items-center gap-4`}>
        <span>{children}</span>
        {sortBy === field && (
          sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
        )}
      </div>
    </th>
  );

  if (records.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 text-center">
        <FileText size={48} className="mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">
          {t('noRecordsFound')}
        </h3>
        <p className="text-slate-500">
          {t('adjustFilters')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <SortableHeader field="userName">{t('userColumn')}</SortableHeader>
              <SortableHeader field="clientName">{t('guestColumn')}</SortableHeader>
              <SortableHeader field="collectionDate">{t('collectionDate')}</SortableHeader>
              <SortableHeader field="depositAmount">{t('depositAmount')}</SortableHeader>
              <SortableHeader field="paymentMethod">{t('depositMethodColumn')}</SortableHeader>
              <SortableHeader field="notes">{t('notesColumn')}</SortableHeader>
              <SortableHeader field="requestId">{t('requestIdColumn')}</SortableHeader>
              <SortableHeader field="inputDateTime">{t('inputDateTime')}</SortableHeader>
              <SortableHeader field="paymentId">{t('receiptId')}</SortableHeader>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {records.map((record) => (
              <tr key={record.id} className="hover:bg-slate-50 transition-colors duration-200">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-slate-900">{record.userName || 'Unknown User'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-slate-900">{record.clientName || 'Unknown Guest'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-slate-900">
                    {record.collectionDate ? new Date(record.collectionDate).toLocaleDateString('en-GB') : '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm font-medium ${record.depositAmount < 0 ? 'text-red-600' : 'text-slate-900'}`}>
                    {record.depositAmount < 0 ? '-' : '+'}{Math.abs(record.depositAmount || 0).toLocaleString()} {t('EGP')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getPaymentMethodBadge(record.paymentMethod || 'Unknown')}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-slate-600 max-w-xs truncate" title={record.notes}>
                    {record.notes || '-'}
                  </div>
                </td>
                <td className={`whitespace-nowrap `}>
                  <div className={` inline-flex items-center font-medium px-2 rounded-[10px] text-sm font-mono ${record.reqStatus === 'cancelled' ? 'bg-orange-100 text-orange-800' : ''}`}>
                    {record.requestId || 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-slate-600">
                    {record.inputDateTime ? new Date(record.inputDateTime).toLocaleString('en-GB') : '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-mono text-slate-900">{record?.paymentId || 'N/A'}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DepositReportTable;